import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject, Input, OnDestroy, OnInit, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Store } from '@ngxs/store';
import dayjs from 'dayjs';
import { Observable } from 'rxjs';
import { AppModulesType } from '../../../../_shared/enums/appModulesType.enum';
import { getBusiness } from '../../../../_shared/enums/business.enum';
import { OriginUrl } from '../../../../_shared/enums/originUrl.enum';
import { AmDurationPipe } from '../../../../_shared/pipes/dayjs-amDuration.pipe';
import { AmRelativePipe } from '../../../../_shared/pipes/dayjs-amRelative.pipe';
import { HelperService, MfToastService } from '../../../../_shared/services';
import { BusinessSubjectIconComponent } from '../../../components/business-subject-icon/business-subject-icon.component';
import { HdAppIconComponent } from '../../../components/hd-app-icon/hd-app-icon.component';
import { CallStateEnum } from '../call-state.enum';
import { WebSocketChannel } from '../model/webSocketChannel.model';
import { WebSocketChannelInfo } from '../model/webSocketChannelInfo.model';
import { TelemedicineService } from '../telemedicine.service';
import { TelmChatComponent } from '../telm-chat/telm-chat.component';
import { ReservationType } from './../../../../_shared/enums/schedule/reservationType.enum';
import { Event } from './../../../../_shared/model/event.model';
import { ActiveModulesService } from './../../../../_shared/services/activeModules.service';
import { BaseState } from './../../../../_shared/state/base/base.state';
import { ReservationService } from './../../home/_services/reservation.service';
import { TelephonyService } from './../../home/telephony/telephony.service';

@Component({
    selector: 'telemedicine-term-card',
    templateUrl: './term-card.component.html',
    styleUrls: ['./term-card.component.scss'],
    imports: [BusinessSubjectIconComponent, HdAppIconComponent, TelmChatComponent, NgTemplateOutlet, DatePipe, AmDurationPipe, AmRelativePipe],
})
export class TermCardComponent implements OnInit, OnDestroy {
    private telemedService = inject(TelemedicineService);
    private helper = inject(HelperService);
    private http = inject(HttpClient);
    private mfToast = inject(MfToastService);
    private telephonyService = inject(TelephonyService);
    private reservation = inject(ReservationService);
    private store = inject(Store);
    private sanitize = inject(DomSanitizer);
    am = inject(ActiveModulesService);

    public selectedTerm: Event;
    public callStartBeforeLimit = 60 * 15;
    public callStartAfterLimit = 60 * 60;
    public canStartCall = false;
    public canStartCallReason = null;
    public displaySendEmailButton = false;

    public patientReady: { status: boolean; userData: WebSocketChannel } = { status: false, userData: null };
    private patientReady$;

    // TODO Ignored with eslint-interactive on 2023-11-10
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public outsideCallInProgress: any; // boolean | WebSocketChannel = false;
    private outsideCallInProgress$;

    // TODO Ignored with eslint-interactive on 2023-11-10
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public connectionStatus: any;
    private connectionStatus$;

    public callState: CallStateEnum;
    private callState$;
    private currentCallChannel$;

    private intervalChecker;

    public callStates = CallStateEnum;

    public callChannelId = null;

    public channelInfo: WebSocketChannelInfo = null;
    private channelInfo$;

    public testDeviceUrl;

    // TODO Ignored with eslint-interactive on 2023-11-10
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public bs: any;
    // TODO Ignored with eslint-interactive on 2023-11-10
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public bsInstructions: any;

    private selectedContractor: number = this.store.selectSnapshot(BaseState.activeContractorId);

    public isHDActive = false;

    // private testDate = dayjs().add(61, 'm');

    @Input()
    set termData(data: Event) {
        if (data !== undefined) {
            this.selectedTerm = data;
            this.selectedTerm.callStart = dayjs(this.selectedTerm.start).subtract(this.callStartBeforeLimit, 'seconds');
            this.selectedTerm.duration = dayjs(this.selectedTerm.end).diff(this.selectedTerm.start, 'minutes');
            this.checkAndInitChannel();
        }
    }

    @Input()
    public showChat = true;

    @Input()
    public showLogo = false;

    @Input()
    // TODO Ignored with eslint-interactive on 2023-11-10
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    set bsId(data: any) {
        this.bs = getBusiness(data);
    }

    // @Input()
    // hideVideoCall: boolean = false;

    ngOnDestroy(): void {
        this.unsubscribeTelemed();
    }
    public unsubscribeTelemed() {
        //@TODO Check if current channel in use, If not, close it!
        //this.telemedService.leaveCall();
        clearInterval(this.intervalChecker);
        this.patientReady$.unsubscribe();
        this.callState$.unsubscribe();
        this.currentCallChannel$.unsubscribe();
        this.outsideCallInProgress$.unsubscribe();
        this.connectionStatus$.unsubscribe();
        this.channelInfo$.unsubscribe();
        if (this.callState !== CallStateEnum.CALL_STARTED) {
            this.telemedService.leaveCall();
        }
        // this.telemedService.leaveCall();

        this.telemedService.closeTelemed(this.selectedTerm.prereservationId);
    }

    ngOnInit() {
        this.initComponent();
        this.insuranceInstructions();
        this.isHDActive = this.am.isAM(AppModulesType.HELLO_DOCTOR);
    }

    private initComponent(): void {
        this.initSocket();
        this.testDeviceUrl = this.helper.getToolsUrl() + '/test-device';
        this.patientReady$ = this.telemedService.getPatientReady(this.selectedTerm.prereservationId).subscribe(ready => {
            this.patientReady = ready;
        });

        this.callState$ = this.telemedService.getCallState().subscribe(callState => {
            if (this.callState !== callState && callState === CallStateEnum.NONE) {
                this.checkAndInitChannel();
            }
            this.callState = callState;
        });

        this.currentCallChannel$ = this.telemedService.getCurrentCallChannel().subscribe(channelId => {
            this.callChannelId = channelId;
        });

        this.outsideCallInProgress$ = this.telemedService.getOutsideCallInProgress().subscribe(inProgress => {
            this.outsideCallInProgress = inProgress;
        });

        this.channelInfo$ = this.telemedService.getChannelInfo(this.selectedTerm.prereservationId).subscribe(channelInfo => {
            this.channelInfo = channelInfo;
        });

        this.connectionStatus$ = this.telemedService.getConnectionStatus(this.selectedTerm.prereservationId).subscribe(status => {
            this.connectionStatus = status;
        });

        /*
        Check if we are ready to start checking call every 1 minute
         */

        this.intervalChecker = setInterval(() => {
            this.checkAndInitChannel();
        }, 10000);
    }

    initSocket() {
        this.telemedService.initTelemed(this.selectedTerm.prereservationId);
    }

    checkAndInitChannel() {
        if (!this.selectedTerm.callStart.isBefore()) {
            this.canStartCall = false;
            this.canStartCallReason = 'before';
            //this.telemedService.initTelemed(this.selectedTerm.prereservationId);
        } else if (!dayjs(this.selectedTerm.end).add(this.callStartAfterLimit, 'seconds').isAfter()) {
            this.canStartCall = false;
            this.canStartCallReason = 'after';
        } else {
            //this.telemedService.initTelemed(this.selectedTerm.prereservationId);
            this.canStartCallReason = null;
            this.canStartCall = true;
        }
        if (
            dayjs(this.selectedTerm.start).subtract(2, 'h').isBefore() &&
            dayjs(this.selectedTerm.end).add(this.callStartAfterLimit, 'seconds').isAfter() &&
            this.selectedTerm.reservationType != ReservationType.HDD &&
            this.selectedTerm.reservationType != ReservationType.HDP &&
            this.selectedTerm.reservationType != ReservationType.SAVAD &&
            this.selectedTerm.reservationType != ReservationType.SAVAP &&
            this.selectedTerm.reservationType != ReservationType.MDFTP &&
            this.selectedTerm.reservationType != ReservationType.MDFTD &&
            this.selectedTerm.reservationType != ReservationType.DRMERKURD &&
            this.selectedTerm.reservationType != ReservationType.DRMERKURP
        ) {
            this.displaySendEmailButton = true;
        } else {
            this.displaySendEmailButton = false;
        }
    }

    initCall() {
        this.telemedService.initCall(this.selectedTerm.prereservationId, this.canStartCallReason ? CallStateEnum.DEVICE_TEST : null);
    }

    checkCallSettings() {
        this.telemedService.checkCallSettings(this.selectedTerm.prereservationId);
    }

    resendEmail() {
        this.reservation.sendTelemedUrl(this.selectedContractor, this.selectedTerm?.prereservationId).subscribe(
            () => {
                this.mfToast.success('Povezava je bila uspešno poslana stranki na njen e-naslov');
            },
            () => {
                this.mfToast.error('Pri pošiljanju povezave stranki je prišlo do napake.');
            },
        );
        //http://localhost:9090/booking/erp/telemed/contractors/54/notify/8a7a65b7-e79b-429e-8835-b65201ee79b2
    }

    public makePhoneCall(): void {
        if (this.selectedTerm?.customer?.phone) {
            this.telephonyService.show(this.selectedTerm?.customer?.phone);
            this.telephonyService.callNumber(this.selectedTerm?.customer?.phone);
        } else {
            this.mfToast.warning('Stranka nima vnešene telefonske številke.');
        }
    }

    public insuranceInstructions() {
        if (this.bs?.id || this.bs?.bsId) {
            this.getInsuranceInstructions(this.bs?.id || this.bs?.bsId).subscribe(res => {
                this.bsInstructions = this.sanitize.sanitize(SecurityContext.HTML, res[0]?.content?.rendered);
            });
        }
    }

    // TODO Ignored with eslint-interactive on 2023-11-10
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getInsuranceInstructions(bsid: any): Observable<any> {
        let app = '';
        switch (this.selectedTerm?.reservationType) {
            case ReservationType.HDP:
            case ReservationType.HDD:
                app = '?hdApp=' + 'hd';
                break;
            case ReservationType.SAVAP:
            case ReservationType.SAVAD:
                app = '?hdApp=' + 'sava';
                break;
            case ReservationType.MDFTP:
            case ReservationType.MDFTD:
                app = '?hdApp=' + 'medifit';
                break;
            case ReservationType.LAIFP:
            case ReservationType.LAIFD:
                app = '?hdApp=' + 'laif';
                break;
            case ReservationType.DRMERKURP:
            case ReservationType.DRMERKURD:
                app = '?hdApp=' + 'drmerkur';
                break;
            case ReservationType.WIDGET_VOUCHER:
                app = '?hdApp=' + 'widget-voucher';
                break;
            case ReservationType.WIDGET:
                if (this.selectedTerm?.originUrl === OriginUrl.G100_01) {
                    app = '?hdApp=' + 'G100-1';
                } else if (this.selectedTerm?.originUrl === OriginUrl.G100_03) {
                    app = '?hdApp=' + 'G100-3';
                } else if (this.selectedTerm?.originUrl === OriginUrl.LAIF) {
                    app = '?hdApp=' + 'laif-spec';
                } else {
                    app = '?bsid=' + bsid;
                }
                break;
            default:
                app = '?bsid=' + bsid;
        }
        return this.http.get(`https://onboarding.medifit.si/wp-json/wp/v2/mfmedical/` + app);
    }
}
